<template>
  <div>
    <SDialog v-model:open="modalVisibility">
      <SDialogContent
        class="promocode-campaigns-tickets-modal !w-[760px] !max-w-[760px] !max-h-[85vh] !p-0 !gap-0 !grid-rows-[auto_1fr_auto]"
        data-cy="campaign-tickets-modal"
        @open-auto-focus.prevent
      >
        <SDialogHeader
          class="space-y-4 border-b border-[#e8e8e8] px-6 pb-5 pt-6 text-left"
        >
          <SDialogTitle class="text-lg font-normal text-[#202223]">
            {{ $t("promo-campaigns.select-items") }}
            <span class="font-semibold">
              {{ promocodeData?.name }}
              <template v-if="promocodeDiscountDisplay">
                {{ promocodeDiscountDisplay }}
              </template>
            </span>
          </SDialogTitle>
          <div class="search-container">
            <input
              class="search-input"
              type="search"
              :placeholder="$t('input.promocode-campaigns') + '...'"
              v-model="searchQuery"
            />
          </div>
        </SDialogHeader>

        <div class="ticket-list-wrapper">
          <template v-if="groupedPSSList.length">
            <div
              v-for="(pss, idx) in groupedPSSList"
              :key="pss.index"
              class="ticket-row"
              :class="{ 'ticket-row--last': idx === groupedPSSList.length - 1 }"
              data-cy="campaign-ticket-row"
            >
              <div class="ticket-name">
                <div class="ticket-name-row">
                  <span class="ticket-category">{{ pss.categoryName }}</span>
                  <span class="ticket-sep">|</span>
                  <span class="ticket-type">
                    {{
                      pss.pssType === PSSType[PSSType.Retail]
                        ? $t("promo-campaigns.retail")
                        : $t("promo-campaigns.ticket")
                    }}
                  </span>
                  <span class="ticket-sep">|</span>
                  <span class="ticket-price">{{ pss.priceName }}</span>
                </div>
                <div
                  v-if="getTicketAppliedDiscountCodes(pss).length"
                  class="pss-discounts"
                >
                  <p
                    class="pss-applied-discounts"
                    v-for="line of getTicketAppliedDiscountCodes(pss)"
                    :key="line.code"
                  >
                    <SvgIcon
                      :iconContent="CouponIcon"
                      iconAlt=""
                      iconStyle="width: 14px; height: 14px"
                    />
                    {{ line.code }}
                    <span class="font-bold">({{ line.percentage }}%)</span>
                  </p>
                </div>
                <div
                  v-if="
                    pss.pssType === PSSType[PSSType.Ticket] && pss.startDateTime
                  "
                  class="pss-timeslot"
                >
                  {{ formatDateTime(pss.startDateTime) }}
                </div>
              </div>
              <div class="ticket-row-right">
                <div class="ticket-qty">
                  <button
                    type="button"
                    class="qty-button"
                    @click="decrementPssCount(pss.index)"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    class="qty-input"
                    v-model.number="pssCount[pss.index]"
                  />
                  <button
                    type="button"
                    class="qty-button"
                    :disabled="pssCount[pss.index] === pss.quantity"
                    @click="incrementPssCount(pss.index)"
                  >
                    +
                  </button>
                </div>
                <SButton
                  type="button"
                  size="sm"
                  class="max-btn"
                  :disabled="pssCount[pss.index] === pss.quantity"
                  @click="setPssCount(pss.index, pss.quantity)"
                >
                  Max (x{{ pss.quantity }})
                </SButton>
              </div>
            </div>
          </template>
          <div v-else class="empty-state">
            <p>{{ $t("promo-campaigns.no-matching-tickets") }}</p>
          </div>
        </div>

        <SDialogFooter
          class="!flex-row !justify-between !items-center border-t border-[#e8e8e8] px-6 py-4 gap-3"
        >
          <SButton
            type="button"
            variant="outline"
            class="min-w-[120px]"
            @click="hideDialog"
          >
            {{ $t("button.cancel") }}
          </SButton>
          <div class="flex gap-3">
            <SButton
              type="button"
              variant="outline"
              class="min-w-[180px]"
              :loading="isapplyToAllLoading"
              :disabled="isapplyToSomeLoading"
              @click="applyToAllTickets"
              data-cy="apply-to-all-btn"
            >
              {{ $t("button.apply-to-all") }}
            </SButton>
            <SButton
              type="button"
              class="min-w-[140px]"
              :loading="isapplyToSomeLoading"
              :disabled="isapplyToAllLoading"
              @click="applyToSelectedTickets"
            >
              {{ $t("button.confirm") }}
            </SButton>
          </div>
        </SDialogFooter>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, PropType, ref } from "vue";
import { useI18n } from "vue-i18n";
import i18n from "@/lang";
import { ModalComponents, ModalStatus } from "@/constants/modal";
import { dialogVisibleForStatus, hideDialog } from "@/utils/vue-helpers/modal";
import { groupedPSS } from "@/utils/vue-helpers/booking";
import {
  BookingStatus,
  IProductSlotState,
  IQuantifiedProductSlotState,
  PSSType,
} from "@/types/db/product-slot-state";
import { processPromocode } from "@/utils/vue-helpers/promocode";
import { IPromocodeCardData } from "@/types/db/promocode-campaign";
import BookingModule from "@/store/modules/booking";
import { AccountingState, TransactionTypes } from "@/types/db/accounting";
import NotificationService from "@/services/notification.service";
import { formatDateTime } from "@/utils/helpers/dates";
import { getPSSValue } from "@/utils/helpers/product-slot-state";
import { sumArray } from "@/utils/helpers/common";
import CouponIcon from "@/assets/images/svg/coupon.svg";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
} from "@smeetz/ui-core";

export default defineComponent({
  name: ModalComponents.PromocodeCampaignsTickets,
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SDialogHeader,
    SDialogTitle,
  },
  props: {
    modalProps: {
      type: Object as PropType<{
        referrer?: ModalComponents;
        goBack?: boolean;
        props?: IPromocodeCardData;
      }>,
      required: true,
    },
  },
  setup(props) {
    const { t } = useI18n();
    const modalVisibility = dialogVisibleForStatus(
      ModalStatus.PromocodeCampaignsTickets
    );
    const searchQuery = ref("");
    const isapplyToAllLoading = ref(false);
    const isapplyToSomeLoading = ref(false);
    const pssCount = ref<Record<number, number>>({});
    const promocodeData = computed(() => {
      return props.modalProps.props;
    });

    const promocodeDiscountDisplay = computed(() => {
      const data = promocodeData.value;
      if (!data) return "";
      if (data.percentageValue && data.percentageValue > 0) {
        return `${data.percentageValue}%`;
      }
      if (data.flatValue && data.flatValue > 0) {
        return `${data.flatValue}`;
      }
      return "";
    });

    const groupedPSSList = computed(() => {
      const gr = groupedPSS(BookingModule.selectedBooking?.pssList || []);
      // exclude canceled and abandoned pss
      // assign an id to each pss group to track quantities later
      return gr
        .filter(
          (pss) =>
            ![BookingStatus.Abandoned, BookingStatus.Canceled].includes(
              pss.bookingStatus as BookingStatus
            ) &&
            (!searchQuery.value ||
              pss.categoryName
                .toLowerCase()
                .includes(searchQuery.value.toLowerCase()) ||
              pss.priceName
                .toLowerCase()
                .includes(searchQuery.value.toLowerCase()))
        )
        .map((pss, index) => {
          return { ...pss, index };
        });
    });

    function incrementPssCount(index: number) {
      pssCount.value[index]++;
    }

    function decrementPssCount(index: number) {
      if (pssCount.value[index] === 0) {
        return;
      }
      pssCount.value[index]--;
    }

    function setPssCount(index: number, qty: number) {
      pssCount.value[index] = qty;
    }

    async function applyToSelectedTickets() {
      if (!promocodeData.value) return;
      const pssList = BookingModule.selectedBooking?.pssList;
      if (!pssList) return;
      isapplyToSomeLoading.value = true;
      const pssListToApplyCodeOn: IProductSlotState[] = [];
      let hasTicketsSelected = false;
      for (const groupedPssIndex in pssCount.value) {
        const numTickets = pssCount.value[groupedPssIndex];
        if (numTickets === 0) {
          continue;
        }
        hasTicketsSelected = true;
        // get pss uuids to apply code on
        const pssUuids = groupedPSSList.value.find(
          (groupedPSS) => groupedPSS.index === Number(groupedPssIndex)
        )?.pssIds;
        if (!pssUuids) continue;
        const filteredPss = pssList.filter((pss) =>
          pssUuids.includes(String(pss.uuid))
        );
        pssListToApplyCodeOn.push(...filteredPss.slice(0, numTickets));
      }
      if (!hasTicketsSelected) {
        NotificationService.showCustomInfoWithTitle(
          t("promo-campaigns.select-item-to-apply"),
          "Info"
        );
        isapplyToSomeLoading.value = false;
        return;
      }
      await processPromocode(promocodeData.value.code, pssListToApplyCodeOn);
      isapplyToSomeLoading.value = false;
      hideDialog();
    }

    async function applyToAllTickets() {
      // prevent multiple clicks at the same time
      if (!promocodeData.value) return;
      isapplyToAllLoading.value = true;
      await processPromocode(promocodeData.value.code);
      isapplyToAllLoading.value = false;
      hideDialog();
    }

    onMounted(() => {
      const pssList = BookingModule.selectedBooking?.pssList;
      const accountings = BookingModule.selectedBooking?.accountings;
      if (!pssList || !accountings) {
        return;
      }
      for (const groupedPss of groupedPSSList.value) {
        const ticketsWithPromocodeApplied = pssList.filter((pssInBooking) => {
          return (
            pssInBooking.uuid &&
            groupedPss.pssIds.includes(pssInBooking.uuid) &&
            accountings.find(
              (acc) =>
                acc.promocode &&
                acc.promocode === promocodeData.value?.code &&
                acc.refund !== 1 &&
                acc.transactionType ===
                  TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
                (acc.state === AccountingState.Completed ||
                  acc.state === AccountingState.Pending) &&
                acc.accountingEntriesList.some(
                  (ae) => ae.productSlotStateUuid === pssInBooking.uuid
                )
            )
          );
        });
        pssCount.value[groupedPss.index] = ticketsWithPromocodeApplied.length;
      }
    });

    function getTicketAppliedDiscountCodes(
      pssGroup: IQuantifiedProductSlotState
    ): { code: string; percentage: string }[] {
      const promocodeAccountings =
        BookingModule.selectedBooking?.accountings.filter(
          (acc) =>
            acc.promoCodeId &&
            acc.refund !== 1 &&
            acc.transactionType ===
              TransactionTypes.TRANSACTION_TYPE_DISCOUNT &&
            (acc.state === AccountingState.Completed ||
              acc.state === AccountingState.Pending)
        );

      if (!promocodeAccountings || !promocodeAccountings.length) {
        // there are no promocode discount accountings
        return [];
      }

      const rowPssListUuids = pssGroup.pssIds;
      const pssGroupPricesSum = pssGroup.quantity * getPSSValue(pssGroup);
      const lines: { code: string; percentage: string }[] = [];
      for (const acc of promocodeAccountings) {
        const promocodeAEs = acc.accountingEntriesList.filter((ae) =>
          rowPssListUuids.some(
            (element) => element === ae.productSlotStateUuid && ae.value > 0
          )
        );
        if (!promocodeAEs || !promocodeAEs.length) continue;
        const pssGroupDiscountsSum = sumArray(
          promocodeAEs.map((it) => it.value)
        );
        const newPriceSum = pssGroupPricesSum - pssGroupDiscountsSum;
        const percentage = (
          ((pssGroupPricesSum - newPriceSum) / pssGroupPricesSum) *
          100
        ).toFixed(0);
        const line = { code: String(acc.promocode), percentage };

        lines.push(line);
      }
      return lines;
    }
    return {
      t,
      modalVisibility,
      searchQuery,
      isapplyToAllLoading,
      isapplyToSomeLoading,
      hideDialog,
      groupedPSSList,
      pssCount,
      incrementPssCount,
      decrementPssCount,
      setPssCount,
      applyToSelectedTickets,
      applyToAllTickets,
      promocodeData,
      promocodeDiscountDisplay,
      formatDateTime,
      PSSType,
      getTicketAppliedDiscountCodes,
      CouponIcon,
    };
  },
});
</script>

<style lang="scss">
@use "@/styles/variables.scss" as *;

.promocode-campaigns-tickets-modal {
  .search-container {
    position: relative;
  }

  .search-input {
    width: 100%;
    height: 44px;
    padding: 0 14px;
    font-size: 14px;
    font-weight: 400;
    color: #333333;
    background-color: #ffffff;
    border: 1px solid #d8d8d8;
    border-radius: 6px;
    transition: border-color 0.15s ease;

    &::placeholder {
      color: #b0b0b0;
    }

    &:focus {
      outline: none;
      border-color: var(--primary-smeetz);
      box-shadow: 0 0 0 3px rgba(var(--primary-smeetz-rgb, 0, 161, 158), 0.12);
    }
  }

  .ticket-list-wrapper {
    overflow-x: hidden;
    overflow-y: auto;
    padding: 8px 24px 16px;
    min-width: 0;
  }

  .empty-state {
    height: 220px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8a8a8e;
    font-size: 14px;
  }

  .ticket-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 14px 0;
    border-bottom: 1px solid #ececec;
    min-width: 0;

    &.ticket-row--last {
      border-bottom: none;
    }
  }

  .ticket-name {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    flex: 1 1 auto;
  }

  .ticket-name-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    color: #202223;
    font-size: 14px;
    line-height: 1.4;
  }

  .ticket-category {
    font-weight: 700;
  }

  .ticket-sep {
    color: #c4c4c4;
  }

  .pss-discounts {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  .pss-applied-discounts {
    margin: 0;
    color: $color-green-eagle;
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    line-height: 1;
  }

  .pss-timeslot {
    font-size: 12px;
    color: #8a8a8e;
  }

  .ticket-row-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }

  .ticket-qty {
    display: flex;
    align-items: stretch;
  }

  .qty-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 34px;
    width: 34px;
    background-color: #ffffff;
    border: 1px solid #d8d8d8;
    color: #333333;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
    font-weight: 500;
    user-select: none;
    transition: background-color 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled) {
      background-color: #f5f5f5;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &:first-child {
      border-radius: 6px 0 0 6px;
      border-right: none;
    }

    &:last-child {
      border-radius: 0 6px 6px 0;
      border-left: none;
    }
  }

  .qty-input {
    height: 34px;
    width: 56px;
    text-align: center;
    background-color: #ffffff;
    border: 1px solid #d8d8d8;
    border-left: none;
    border-right: none;
    font-size: 14px;
    font-weight: 600;
    color: #202223;
    margin: 0;
    outline: none;
    border-radius: 0;
  }

  .qty-input::-webkit-inner-spin-button,
  .qty-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .qty-input[type="number"] {
    -moz-appearance: textfield;
  }

  .max-btn {
    min-width: 86px;
  }
}
</style>
